import * as React from 'react'
import { Helmet } from 'react-helmet'
import { useHistory } from 'react-router-dom'
import { RouteComponentProps } from 'react-router'
import { useFlags } from 'launchdarkly-react-client-sdk'

import ResetPassword from './ResetPassword'
import ApiKeysTable from './ApiKeysTable'
import UserDetails from './UserDetails'
import Mfa from './MFA'

import theme from 'theme'
import useAgent from 'hooks/use-agent'
import { withAuthContext } from 'context/with-auth-context'
import { getUserDisplayName, UserIdentity } from 'utils/user'
import { ApiKeysResponse } from 'api'
import { PanelDetails } from 'components/attacks/details/AttackStyles'
import Header, { Heading } from 'components/navigation/Header'
import Content from 'components/common/Content'
import { Tab, TabList, TabPanel, TabPanels, Tabs } from 'components/common/Tabs'
import Text from 'components/common/Text'
import IconCounterGroup from 'components/rbac-settings/IconCounterGroup'

// memoized so it does not re-render unnecessarily
// Profile re-renders on a regular basis because of AuthenticatedRoutes' polled profile called
const ProfileContents: React.FC<{
  session: UserIdentity & {
    teams: Record<string, any> // TODO does UserIdentity need to be updated? this maybe should come from api.d.ts
  }
  tab: string
}> = React.memo(({ session, tab }) => {
  const history = useHistory()
  const teams = session?.teams ?? []
  const { userLevelApiKeys: isRbacApiEnabled } = useFlags()

  const {
    data: apiKeys,
    isLoading: isLoadingApiKeys,
    hasError: hasErrorApiKeys,
    doFetch
  }: {
    data: ApiKeysResponse
    isLoading: boolean
    hasError: boolean
    doFetch: () => void
  } = useAgent('/apikeys', [], { skipRequest: !isRbacApiEnabled })

  const tabs = [
    'details',
    'security',
    isRbacApiEnabled ? 'apikeys' : ''
  ].filter(Boolean)
  const activeTabIndex = tabs.findIndex(t => t === tab)

  return (
    <>
      {isRbacApiEnabled && (
        <>
          <Text as="h1">{getUserDisplayName(session)}</Text>
          <IconCounterGroup
            counts={[
              {
                icon: 'key',
                label: 'API Key',
                count: apiKeys?.length
              },
              {
                icon: 'users',
                label: 'Team',
                count: teams.length
              }
            ]}
            css={{
              marginBottom: theme.space[3]
            }}
          />
        </>
      )}

      <Tabs
        index={activeTabIndex > -1 ? activeTabIndex : 0}
        onChange={route => {
          return history.replace(`/profile/${tabs[route]}`)
        }}
      >
        <TabList>
          <Tab data-test-tab="details">Details</Tab>
          <Tab data-test-tab="security">Security</Tab>
          {isRbacApiEnabled && <Tab data-test-tab="apikeys">API Keys</Tab>}
        </TabList>
        <TabPanels>
          <TabPanel>
            <UserDetails user={session} />
          </TabPanel>
          <TabPanel>
            <PanelDetails>
              <ResetPassword />
              <Mfa session={session} />
            </PanelDetails>
          </TabPanel>
          {isRbacApiEnabled && (
            <TabPanel>
              <ApiKeysTable
                apiKeysLimit={5}
                doRefresh={doFetch}
                hasError={hasErrorApiKeys}
                isLoading={isLoadingApiKeys}
                keys={apiKeys ?? []}
              />
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
    </>
  )
})

type ProfileProps = {
  authState: Record<string, any>
}
type ProfileRouteProps = ProfileProps & RouteComponentProps<{ tab: string }>

const Profile: React.FC<ProfileRouteProps> = ({ authState, match }) => {
  return (
    <>
      <Helmet title="Account Settings" />
      <Header>
        <Heading>Account Settings</Heading>
      </Header>
      <Content id="Profile">
        <ProfileContents session={authState.session} tab={match.params.tab} />
      </Content>
    </>
  )
}

export default withAuthContext(Profile)
